import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ShieldCheck, Check, CalendarDays, ClipboardList, Truck, Award, ArrowRight, ChevronDown } from 'lucide-react';
import { api } from '../lib/api';
import usePageContent, { money } from '../hooks/usePageContent';
import RentalForm from '../components/RentalForm';
import useSEO from '../hooks/useSEO';

const STEPS = [
    { icon: ClipboardList, title: 'Pick a machine', text: 'Choose from the list below.' },
    { icon: CalendarDays, title: 'Choose dates', text: 'By the day or by the week.' },
    { icon: Truck, title: 'Pickup or delivery', text: 'We confirm within one business day.' },
];

const FleetRow = ({ m, onBook }) => (
    <div className="grid grid-cols-[72px_1fr] sm:grid-cols-[88px_1fr_auto] gap-4 items-center p-4 border-b border-neutral-200 last:border-b-0" data-testid={`rental-fleet-${m.id}`}>
        <Link to={`/machines/${m.slug || m.id}`} className="block aspect-square bg-neutral-50 border border-neutral-200">
            {m.image_url && <img src={m.image_url} alt={m.name} loading="lazy" className="w-full h-full object-contain p-1.5" />}
        </Link>
        <div className="min-w-0">
            <Link to={`/machines/${m.slug || m.id}`} className="font-display font-black uppercase text-lg text-neutral-900 hover:text-brand-primary transition-colors">{m.name}</Link>
            <span className={`ml-2 align-middle label-caps text-[10px] px-1.5 py-0.5 ${m.condition === 'used' ? 'bg-amber-100 text-amber-800' : 'bg-emerald-100 text-emerald-800'}`} data-testid={`rental-condition-${m.id}`}>{m.condition}</span>
            <div className="mt-1 flex flex-wrap gap-x-5 gap-y-1 text-sm text-neutral-700">
                <span><b className="text-neutral-900" data-testid={`rental-daily-${m.id}`}>{m.rental_daily ? money(m.rental_daily) : 'Call'}</b> / day</span>
                <span><b className="text-neutral-900" data-testid={`rental-weekly-${m.id}`}>{m.rental_weekly ? money(m.rental_weekly) : 'Call'}</b> / week</span>
                {m.rental_deposit > 0 && <span className="text-neutral-500">{money(m.rental_deposit)} refundable deposit</span>}
            </div>
            {m.rental_specs && <p className="mt-1.5 text-xs text-neutral-500 whitespace-pre-line" data-testid={`rental-specs-${m.id}`}>{m.rental_specs}</p>}
        </div>
        <button onClick={() => onBook(m.id)} className="col-span-2 sm:col-span-1 bg-brand-primary hover:bg-brand-primaryHover text-white font-bold px-5 py-3 label-caps text-xs transition-colors" data-testid={`rental-book-${m.id}`}>
            Book this machine
        </button>
    </div>
);

const Terms = ({ terms }) => (
    <details className="border border-neutral-200 bg-neutral-50 group" data-testid="rental-terms-box">
        <summary className="cursor-pointer list-none p-4 flex items-center justify-between label-caps text-sm text-neutral-900">
            <span className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-brand-primary" /> Rental terms</span>
            <ChevronDown className="h-4 w-4 group-open:rotate-180 transition-transform" />
        </summary>
        <ul className="px-4 pb-4 space-y-2" data-testid="rental-terms">
            {terms.map((t, i) => <li key={i} className="flex gap-2 text-sm text-neutral-700"><Check className="h-4 w-4 text-brand-primary mt-0.5 flex-shrink-0" />{t}</li>)}
        </ul>
    </details>
);

const Rental = () => {
    const content = usePageContent();
    const [fleet, setFleet] = useState([]);
    const [machineId, setMachineId] = useState('');
    const [params] = useSearchParams();
    useSEO({ title: 'Dry Ice Blasting Equipment Rental', description: 'Rent professional dry ice blasting machines by the day or week, with pickup or delivery from Dry Ice Bulls.' });
    useEffect(() => { api.get('/rentals/catalog').then((r) => setFleet(r.data)); }, []);
    useEffect(() => { if (params.get('machine')) setMachineId(params.get('machine')); }, [params]);
    const book = (id) => {
        setMachineId(id);
        document.getElementById('book')?.scrollIntoView({ behavior: 'smooth' });
    };

    return (
        <div data-testid="rental-page">
            <section className="border-b border-neutral-200 bg-neutral-50">
                <div className="container-page pt-32 pb-10">
                    <div className="label-caps text-brand-primary mb-4">/ Rental</div>
                    <h1 className="font-display font-black uppercase tracking-tighter text-5xl md:text-6xl text-neutral-900">Rent a dry ice blaster</h1>
                    <p className="mt-4 text-neutral-600 max-w-2xl" data-testid="rental-intro">{content?.rental_intro}</p>
                    <ol className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="rental-steps">
                        {STEPS.map(({ icon: Icon, title, text }, i) => (
                            <li key={title} className="flex items-center gap-3 bg-white border border-neutral-200 p-4">
                                <span className="font-display font-black text-2xl text-brand-primary">{i + 1}</span>
                                <Icon className="h-5 w-5 text-neutral-900 flex-shrink-0" />
                                <div><div className="font-bold text-neutral-900 text-sm">{title}</div><div className="text-xs text-neutral-500">{text}</div></div>
                            </li>
                        ))}
                    </ol>
                    {params.get('cancelled') && <p className="mt-4 text-amber-700" data-testid="rental-cancelled">Deposit payment was cancelled — your request was saved but isn&apos;t confirmed yet. Submit again or call us.</p>}
                </div>
            </section>

            <section className="container-page py-12">
                <h2 className="font-display font-black uppercase text-2xl md:text-3xl text-neutral-900 mb-5">Machines & rates</h2>
                {fleet.length === 0 ? (
                    <p className="text-neutral-500" data-testid="rental-fleet-empty">Call (984) 201-0767 for current rental availability.</p>
                ) : (
                    <div className="border border-neutral-200 bg-white" data-testid="rental-fleet">{fleet.map((m) => <FleetRow key={m.id} m={m} onBook={book} />)}</div>
                )}
                <Link to="/training" className="mt-4 flex items-center justify-between gap-3 border border-neutral-200 p-4 hover:border-brand-primary transition-colors" data-testid="rental-training-link">
                    <span className="flex items-center gap-3 text-sm text-neutral-700"><Award className="h-5 w-5 text-brand-primary" /> New to dry ice blasting? Get operator training — on-site or remote.</span>
                    <ArrowRight className="h-4 w-4 text-brand-primary flex-shrink-0" />
                </Link>
            </section>

            <section className="border-t border-neutral-200 bg-neutral-50" id="book">
                <div className="container-page py-12">
                    <h2 className="font-display font-black uppercase text-2xl md:text-3xl text-neutral-900 mb-6">Request a booking</h2>
                    <RentalForm fleet={fleet} machineId={machineId} setMachineId={setMachineId} />
                    <div className="mt-8 max-w-3xl"><Terms terms={content?.rental_terms || []} /></div>
                </div>
            </section>
        </div>
    );
};

export default Rental;
